import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { CheckmarkIcon, ClipboardLineIcon, IconSizes } from '@inno-ui/icons'
import { Modal } from '@inno-ui/modal'
import { Spinner, SpinnerSizes } from '@inno-ui/spinner'
import { useTranslation } from 'react-i18next'
import { useArtifactSourceQuery } from 'shared/api/forgeApi'

import { highlightHtml } from '../../../utils/highlightHtml'
import styles from '../ReviewQueue.module.scss'
import { REVIEW_MODAL_CHROME } from './CardShell'

interface CodeModalProps {
  publicId: string
  title: string
  // controlled open state — parent decides when to show
  isOpen: boolean
  onClose: () => void
}

// Source viewer — fetches on demand and renders highlighted HTML. Owns the
// 'Copied' timer + cleanup. Resetting `copied` on close prevents the next
// open from briefly flashing the checkmark.
export const CodeModal: FC<CodeModalProps> = ({
  publicId,
  title,
  isOpen,
  onClose
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })
  const [copied, setCopied] = useState(false)
  const copyTimerRef = useRef<number | null>(null)

  // only fetch while the modal is open — closes don't keep the network busy
  const { data: source, isFetching: sourceLoading } = useArtifactSourceQuery(
    publicId,
    { enabled: isOpen }
  )
  const highlighted = useMemo(() => highlightHtml(source || ''), [source])

  const copyCode = () => {
    navigator.clipboard?.writeText(source || '')
    setCopied(true)
    if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current)
    copyTimerRef.current = window.setTimeout(() => setCopied(false), 1500)
  }

  // clear the "Copied" timer if the card unmounts mid-window to avoid the
  // setState-on-unmounted warning
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current)
    }
  }, [])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setCopied(false)
        onClose()
      }}
      {...REVIEW_MODAL_CHROME}
      maxWidth={900}
      maxHeight='85vh'
      layers={[
        {
          title: t('code_modal_title', { title }),
          bodyPaddings: false,
          body: sourceLoading ? (
            <div className={styles.codeLoading}>
              <Spinner size={SpinnerSizes.M} />
            </div>
          ) : (
            <div className={styles.codeWrap}>
              {/* compact copy control floated over the code so it doesn't take
                  a full footer row (and leave dead white space below it) */}
              <button
                type='button'
                className={styles.copyFloat}
                onClick={copyCode}
              >
                {copied ? (
                  <CheckmarkIcon size={IconSizes.Size16} />
                ) : (
                  <ClipboardLineIcon size={IconSizes.Size16} />
                )}
                {copied ? t('copied') : t('copy')}
              </button>
              <pre className={styles.code}>
                {/* highlighted: source is fully escaped in highlightHtml, only
                    our own spans are injected — safe for innerHTML */}
                <code dangerouslySetInnerHTML={{ __html: highlighted }} />
              </pre>
            </div>
          )
        }
      ]}
    />
  )
}
